import { useEffect, useState, useCallback, useMemo } from "react";
import { CalendarIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { 
  BuildingOffice2Icon,
  UserIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ClockIcon
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { getProjectsAsync } from "../../../slices/project";
import { ModuleFilter } from "../../utils/ModuleFilter";
import { 
  dateToString,
  ItemStatus,
  updateUrlWithException,
} from "../../Utils";
import {
  orderProjectsUnderParents,
  projectParentDepth,
} from "../../../utils/projectListHierarchy";

export const ProjectsStackList = (props) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const itemState = useSelector((store) => {
    return store.projects;
  });

  const [controller, setController] = useState({
    filters: [...(props?.filters || [])],
    page: 1,
    query: "",
    sort: "desc",
    sortBy: "created_at",
    view: "all",
    offer_scope: "projects",
  });

  // Align with ModuleFilter custom checkboxes so the first fetch includes active-only.
  const getInitialFilters = () => {
    return {
      query: "",
      statuses: [],
      active: true,
      with_customer: false,
    };
  };

  const [filters, setFilters] = useState(getInitialFilters());

  // Define accepted statuses for projects based on the enum from the API
  const projectAcceptedStatuses = [
    "draft",
    "ongoing", 
    "finished",
    "cancelled",
    "on_hold",
    "planning",
  ];

  // Define custom checkbox button filters for projects
  const customCheckboxButtonFilters = [
    {
      key: "active",
      label: t("Active Projects"),
      icon: CheckCircleIcon,
      defaultValue: true,
    },
    {
      key: "with_customer",
      label: t("With Customer"),
      icon: BuildingOffice2Icon,
      defaultValue: false,
    },
  ];

  // Update URL params when filters change
  useEffect(() => {
    updateUrlWithException(
      {
        query: filters.query,
        statuses:
          filters.statuses.length > 0 ? JSON.stringify(filters.statuses) : null,
        page: controller.page,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        offer_scope: controller.offer_scope,
      },
      "filters" // Preserve existing filters parameter
    );
  }, [
    filters,
    controller.page,
    controller.sort,
    controller.sortBy,
    controller.view,
  ]);

  const getProjects = useCallback(() => {
    // Create filters array following the reports structure
    const filtersArray = [];

    // Add status filter if statuses are selected
    if (filters?.statuses && filters.statuses.length > 0) {
      filtersArray.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }

    // Add custom checkbox filters
    if (filters?.active) {
      filtersArray.push({
        name: "active",
        value: "true",
      });
    }

    if (filters?.with_customer) {
      filtersArray.push({
        name: "with_customer",
        value: "true",
      });
    }

    // Merge props filters with component filters
    const allFilters = [...(props?.filters || []), ...filtersArray];

    const filteredController = {
      ...controller,
      query: filters?.query || "",
      filters: allFilters,
      ...(props?.fields && { fields: props.fields }),
      ...(props?.per_page && { per_page: props.per_page }),
    };

    const payload = {
      filters: filteredController.filters,
      page: filteredController.page,
      query: filteredController.query,
      sort: filteredController.sort,
      sortBy: filteredController.sortBy,
      view: filteredController.view,
      offer_scope: filteredController.offer_scope,
    };

    dispatch(getProjectsAsync(payload));
  }, [controller, filters, props?.filters, props?.fields, props?.per_page, dispatch]);

  useEffect(() => {
    getProjects();
  }, [getProjects]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const ProjectCard = ({ item, nestDepthPx }) => {
    return (
      <div 
        className="shadow sm:overflow-hidden sm:rounded-md cursor-pointer hover:shadow-lg transition-shadow duration-200"
        onClick={() => navigate("/dashboard/projects/" + item?.id)}
        style={
          nestDepthPx > 0
            ? {
                marginLeft: nestDepthPx,
                borderLeft: "3px solid rgb(226 232 240)",
              }
            : undefined
        }
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {item?.project_number || item?.name || "Missing Title"}
                  </h3>
                  {item?.is_offer ? (
                    <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900">
                      {t("project_list.badge.offer")}
                    </span>
                  ) : null}
                  {item?.parent_project ? (
                    <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                      {t("project_list.badge.subproject")}
                    </span>
                  ) : null}
                  <ItemStatus item={item} />
                </div>
                {item?.parent_project ? (
                  <p className="mt-1 text-xs text-slate-600">
                    {t("project_list.sub_of")}:{" "}
                    <span className="font-medium text-slate-800">
                      {item.parent_project.displayTitle ||
                        item.parent_project.name ||
                        item.parent_project.project_number}
                    </span>
                  </p>
                ) : null}
                <p className="text-sm text-gray-600">{item?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {item?.customer && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <BuildingOffice2Icon className="w-3 h-3 mr-1" />
                  {item.customer.displayTitle || item.customer.name}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-white px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Customer Information */}
            {item?.customer && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <BuildingOffice2Icon className="w-4 h-4 mr-1" />
                  {t("Customer")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.customer.displayTitle || item.customer.name || "-"}
                </dd>
              </div>
            )}

            {/* Delivery Address */}
            {item?.delivery_address && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  {t("Delivery Address")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.delivery_address.displayTitle || "-"}
                </dd>
              </div>
            )}

            {/* Executing Employee */}
            {item?.executing_employee && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <UserIcon className="w-4 h-4 mr-1" />
                  {t("Executing Employee")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.executing_employee.displayTitle || item.executing_employee.name || "-"}
                </dd>
              </div>
            )}

            {/* Agreement Date */}
            {item?.agreement_date && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {t("Agreement Date")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.agreement_date)}
                </dd>
              </div>
            )}

            {/* Project Status */}
            {item?.status && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  {t("Status")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.status}
                </dd>
              </div>
            )}

            {/* Created Date */}
            {item?.created_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {t("Created")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.created_at)}
                </dd>
              </div>
            )}
          </div>

          {/* Description */}
          {item?.description && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">{t("Description")}</dt>
              <dd 
                className="mt-1 text-sm text-gray-900 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const data = itemState?.data;
  const isLoading = itemState?.isLoading;
  const loadError = itemState?.error;

  const Stats = () => {
    const orderedRows = useMemo(
      () => orderProjectsUnderParents(data || []),
      [data]
    );
    return (
      <>
        {data && data.length > 0 && (
          <div className="space-y-4 mt-5">
            {orderedRows.map((item) => (
              <ProjectCard
                key={item?.id}
                item={item}
                nestDepthPx={projectParentDepth(item, orderedRows) * 24}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
      {/* Filter Component */}
      <div className="bg-white shadow sm:rounded-lg p-6">
        <ModuleFilter
          onFiltersChange={handleFiltersChange}
          initialFilters={filters}
          acceptedStatuses={projectAcceptedStatuses}
          searchPlaceholder={t("Search projects...")}
          filterTitle={t("Filter Projects by Status")}
          customCheckboxButtonFilters={customCheckboxButtonFilters}
          segmentedControl={{
            value: controller.offer_scope || "projects",
            onChange: (v) =>
              setController((c) => ({
                ...c,
                page: 1,
                offer_scope: v,
              })),
            options: [
              { value: "projects", label: t("project_list.scope.projects") },
              { value: "offers", label: t("project_list.scope.offers") },
              { value: "all", label: t("project_list.scope.all") },
            ],
          }}
        />
      </div>

      <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
            {isLoading ? (
              <ResourceLoading size="small" />
            ) : loadError ? (
              <div className="text-center py-8 text-red-500">
                <div className="text-lg">Could not load projects</div>
              </div>
            ) : data && data.length > 0 ? (
              <Stats />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-lg">No projects found</div>
                <div className="text-sm">Create a new project</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ProjectsStackList.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "created_at",
  filters: [],
};
